/**
 * DegreeRequirementService
 * 
 * Centralized service for degree requirement verification, progress calculation,
 * and course data utilities. Used by degree planning, degree search, and info-rutgers.
 */

import {
    Degree,
    RutgersCourse,
    UserCourse,
    Requirement,
    RequirementProgress
} from '@/app/types/interfaces';
import { courseListsApi } from './api';
import { courseEquivalenceKey, equivalentCourseCodes } from '@/app/lib/courseEquivalencies';
import { normalizeRutgersBaseCourseId } from '@/app/lib/courseIdFormatter';

// Note: RequirementProgress interface in interfaces.ts might need updates 
// to match the one we had here. I'll check that.

// ============================================================================
// Types
// ============================================================================
// Local types moved to interfaces.ts


// ============================================================================
// Utilities
// ============================================================================

/**
 * Normalize a course code by stripping non-numeric characters.
 */
export const normalize = (code: string | number): string =>
    String(code || '').replace(/[^0-9]/g, '');

/** Saved transfer labels may contain digits, but they are not Rutgers course identities. */
const verifiedCourseCode = (code: string | number): string | null => {
    const baseId = normalizeRutgersBaseCourseId(String(code));
    return baseId ? normalize(baseId) : null;
};

/** One row from `degree_course_lists_direct.csv` after parsing. */
export type DegreeCourseListDirectRow = {
    classes: string[];
    name?: string;
    description?: string;
};

/**
 * Returns degree/CSV course lists that include this catalog course (by baseId),
 * using the same numeric normalization as the rest of degree tooling.
 */
export function findDegreeCourseListsContainingCourse(
    baseId: string,
    degreeCourseListsDirect: Map<string, DegreeCourseListDirectRow>
): Array<{ key: string; label: string; description?: string }> {
    const target = normalize(baseId);
    if (!target) return [];

    const out: Array<{ key: string; label: string; description?: string }> = [];
    degreeCourseListsDirect.forEach((data, key) => {
        const classes = data.classes || [];
        if (classes.some((c) => normalize(c) === target)) {
            out.push({
                key,
                label: data.name || key,
                description: data.description,
            });
        }
    });
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
}

/**
 * Check if a value is likely a course code (not a pool reference).
 */
export const isLikelyCourseCode = (value: string): boolean => {
    if (!value) return false;
    if (value.includes('_')) return false;
    if (value.includes(':')) return true;
    return /\d{3,}/.test(value);
};

/**
 * Multiple matching strategies for course codes.
 */
export const matchesCourse = (
    courseCode: string,
    userCodeSet: Set<string>,
    userCodeNo01Set: Set<string>,
    userCodeOriginalSet?: Set<string>
): boolean => {
    const norm = normalize(courseCode);
    const no01 = norm.replace(/^01/, '');

    return (
        userCodeSet.has(norm) ||
        userCodeNo01Set.has(no01) ||
        (userCodeOriginalSet?.has(courseCode) ?? false) ||
        (userCodeOriginalSet?.has(norm) ?? false)
    );
};

/**
 * Extract course codes from a plan's semesters.
 */
export const extractPlannedCourseCodes = (semesters: any[]): Set<string> => {
    const codes = new Set<string>();
    if (!semesters || !Array.isArray(semesters)) return codes;

    semesters.forEach(sem => {
        const courses = Array.isArray(sem.courses) ? sem.courses : [];
        courses.forEach((c: any) => {
            if (c.kind === 'course' && c.code) {
                codes.add(normalize(c.code));
            }
        });
    });
    return codes;
};

/**
 * Format a course code for display (e.g., 01198111 -> 01:198:111).
 */
export const formatCourseCode = (code: string): string => {
    // If already formatted with colons, return as is
    if (code.includes(':')) {
        return code;
    }
    const norm = normalize(code);
    // Standard 8-digit format: SS DDD CCC (e.g., 01198111 -> 01:198:111)
    if (norm.length === 8) {
        return `${norm.slice(0, 2)}:${norm.slice(2, 5)}:${norm.slice(5, 8)}`;
    }
    // 9-digit format with leading zero: 0SS DDD CCC
    if (norm.length === 9) {
        return `${norm.slice(0, 2)}:${norm.slice(2, 5)}:${norm.slice(5, 8)}`;
    }
    return code;
};

// ============================================================================
// Service Logic
// ============================================================================

export class DegreeRequirementService {
    /**
     * Get credits for a course code using maps from context.
     */
    static getCourseCredits(
        courseCode: string,
        creditsMap: Map<string, number>,
        userPrerequisites?: UserCourse[]
    ): number {
        const norm = normalize(courseCode);
        const verified = verifiedCourseCode(courseCode);
        const formats = [
            courseCode,
            norm,
            norm.replace(/^0+/, ''),
            `01${norm}`,
            `0${norm}`,
        ];

        // Check user overrides first
        if (userPrerequisites) {
            const userCourse = verified
                ? userPrerequisites.find(c => verifiedCourseCode(c.course_code) === verified)
                : undefined;
            if (userCourse?.credits !== undefined) {
                const num = Number(userCourse.credits);
                if (!isNaN(num)) return num;
            }
        }

        // Check course data map
        for (const format of formats) {
            const credits = creditsMap.get(format);
            if (credits !== undefined && credits > 0) return credits;
        }

        return 0;
    }

    /**
     * Calculate median credits for a course list to estimate planned list block values.
     */
    static getMedianCreditsForCourseList(
        listId: number,
        courseListCache: Map<number, { classes: string[] }>,
        creditsMap: Map<string, number>
    ): number {
        const list = courseListCache.get(listId);
        if (!list || !list.classes || list.classes.length === 0) return 3;

        const creditsList = list.classes
            .map(code => this.getCourseCredits(code, creditsMap))
            .filter(c => c > 0)
            .sort((a, b) => a - b);

        if (creditsList.length === 0) return 3;
        const mid = Math.floor(creditsList.length / 2);
        return creditsList.length % 2 !== 0
            ? creditsList[mid]
            : (creditsList[mid - 1] + creditsList[mid]) / 2;
    }

    /**
     * Expand a requirement's course pool.
     */
    static expandPool(
        requirement: Requirement,
        expandedPoolsCache: Map<string, string[]>,
        fallbackCache?: Map<number, { name?: string; classes: string[] }>,
        degreeMappings?: Array<{ name: string; course_list_id: number }>
    ): string[] {
        const rawPool = Array.isArray(requirement.course_pool) ? requirement.course_pool : [];
        const expanded = rawPool.flatMap((entry: string) => {
            // Exact captured pool identities take precedence over the legacy course-code heuristic.
            // Names such as 460Courses and 460Courses-300levelPLUS contain digits too.
            if (expandedPoolsCache.has(entry)) return expandedPoolsCache.get(entry)!.map(normalize);
            if (isLikelyCourseCode(entry)) return [normalize(entry)];

            // Fallback: if not in direct mapping, try to find by ID from old system
            if (fallbackCache && degreeMappings) {
                const mapping = degreeMappings.find(m => m.name === entry);
                if (mapping) {
                    const listData = fallbackCache.get(mapping.course_list_id);
                    if (listData && listData.classes && listData.classes.length > 0) {
                        return listData.classes.map(normalize);
                    }
                }
            }

            return [];
        });
        return Array.from(new Set(expanded));
    }

    /**
     * Calculate progress for a single requirement.
     */
    static calculateRequirementProgress(
        requirement: Requirement,
        context: {
            userPrerequisites: UserCourse[];
            plannedCodes: string[];
            plannedCourseListBlocks: string[];
            expandedPools: Map<string, string[]>;
            courseListCache: Map<number, { classes: string[] }>;
            creditsMap: Map<string, number>;
            degreeMappings: Array<{ name: string; course_list_id: number }>;
        }
    ): RequirementProgress {
        const {
            userPrerequisites,
            plannedCodes,
            plannedCourseListBlocks,
            expandedPools,
            courseListCache,
            creditsMap,
            degreeMappings
        } = context;

        const poolExpanded = this.expandPool(requirement, expandedPools, courseListCache, degreeMappings);
        const hasAvailableCourses = poolExpanded.length > 0;

        const userCodeSet = new Set(userPrerequisites.flatMap(c => verifiedCourseCode(c.course_code) ?? []));
        const userCodeNo01Set = new Set([...userCodeSet].map(c => c.replace(/^01/, '')));
        const plannedCodeSet = new Set(plannedCodes.flatMap(code => verifiedCourseCode(code) ?? []));
        const plannedCodeNo01Set = new Set([...plannedCodeSet].map(c => c.replace(/^01/, '')));

        let completedCourses: string[] = [];
        let plannedCourses: string[] = [];
        let creditsCompleted = 0;
        let creditsPlanned = 0;

        // 1. Identify completed and planned courses from the pool
        if (hasAvailableCourses) {
            const processedCodes = new Set<string>();

            poolExpanded.forEach(poolCode => {
                // Equivalent courses (e.g. CS 111 / CS 121) fill one pool slot.
                const slot = courseEquivalenceKey(poolCode);
                if (processedCodes.has(slot)) return;
                processedCodes.add(slot);

                const slotCodes = [poolCode, ...equivalentCourseCodes(poolCode)];
                const completedCode = slotCodes.find(candidate => matchesCourse(candidate, userCodeSet, userCodeNo01Set));
                const plannedCode = completedCode ? undefined : slotCodes.find(candidate => matchesCourse(candidate, plannedCodeSet, plannedCodeNo01Set));
                const code = completedCode ?? plannedCode ?? poolCode;
                const norm = normalize(code);
                const isCompleted = Boolean(completedCode);
                const isPlanned = Boolean(plannedCode);

                // Requirement diagnostics are intentionally opt-in: this runs for every
                // pool entry and can otherwise flood the browser console during navigation.
                if (isCompleted && process.env.NEXT_PUBLIC_DEBUG_DEGREE_REQUIREMENTS === 'true') {
                    console.log(`[calculateRequirementProgress] Found completed course: ${code} (normalized: ${norm}) in requirement ${requirement.id || requirement.title}`);
                }

                const credits = this.getCourseCredits(code, creditsMap, userPrerequisites);

                if (isCompleted) {
                    completedCourses.push(code);
                    creditsCompleted += credits;
                } else if (isPlanned) {
                    plannedCourses.push(code);
                    creditsPlanned += credits;
                }
            });

            // Debug: log if we have courses but none matched
            if (
                process.env.NEXT_PUBLIC_DEBUG_DEGREE_REQUIREMENTS === 'true' &&
                poolExpanded.length > 0 &&
                completedCourses.length === 0 &&
                userPrerequisites.length > 0
            ) {
                console.warn(`[calculateRequirementProgress] Requirement ${requirement.id || requirement.title} has ${poolExpanded.length} courses in pool but none matched user's ${userPrerequisites.length} completed courses`);
                console.warn(`[calculateRequirementProgress] Sample pool courses: ${poolExpanded.slice(0, 5).join(', ')}`);
                console.warn(`[calculateRequirementProgress] Sample user courses: ${userPrerequisites.slice(0, 5).map(c => c.course_code).join(', ')}`);
            }
        }

        // 2. Handle planned list blocks
        const mappingByName = new Map(degreeMappings.map(m => [m.name, m.course_list_id]));
        const rawPool = requirement.course_pool || [];
        const listNames = rawPool.filter(e => expandedPools.has(e) || !isLikelyCourseCode(e));

        let plannedListBlocks = 0;
        listNames.forEach(name => {
            if (plannedCourseListBlocks.includes(name)) {
                plannedListBlocks += 1;
                const listId = mappingByName.get(name);
                if (listId) {
                    const median = this.getMedianCreditsForCourseList(listId, courseListCache, creditsMap);
                    creditsPlanned += median;
                    plannedCourses.push(`__planned_list_block__${name}`);
                }
            }
        });

        // 3. Cap progress based on requirements
        let neededCourses = requirement.courses_needed || 0;
        let neededCredits = requirement.credits_needed || 0;

        // Estimate credits for display/totals only (not for completion logic)
        // This prevents requirements from contributing 0 to totalCreditsNeeded
        // but doesn't affect whether a requirement is marked as completed
        let estimatedCreditsForDisplay = 0;

        if (hasAvailableCourses) {
            const poolCredits = poolExpanded
                .map(code => this.getCourseCredits(code, creditsMap, userPrerequisites))
                .filter(c => c > 0)
                .sort((a, b) => a - b);

            if (poolCredits.length > 0) {
                // Calculate median credits
                const mid = Math.floor(poolCredits.length / 2);
                const medianCredits = poolCredits.length % 2 !== 0
                    ? poolCredits[mid]
                    : (poolCredits[mid - 1] + poolCredits[mid]) / 2;

                if (neededCourses > 0 && neededCredits === 0) {
                    // For course-based requirements: estimate as median * courses_needed
                    estimatedCreditsForDisplay = medianCredits * neededCourses;
                } else if (neededCourses === 0 && neededCredits === 0) {
                    // For requirements with no explicit count: estimate as median (single course)
                    estimatedCreditsForDisplay = medianCredits;
                }
            }
        }

        // If courses_needed is set but neededCredits is not, estimate neededCredits for completion logic
        // This is separate from displayCreditsNeeded - used only for internal completion checks
        if (neededCourses > 0 && neededCredits === 0 && hasAvailableCourses) {
            const poolCredits = poolExpanded
                .map(code => this.getCourseCredits(code, creditsMap, userPrerequisites))
                .filter(c => c > 0)
                .sort((a, b) => a - b);

            if (poolCredits.length > 0) {
                // Use median * courses_needed for a more accurate estimate
                const mid = Math.floor(poolCredits.length / 2);
                const medianCredits = poolCredits.length % 2 !== 0
                    ? poolCredits[mid]
                    : (poolCredits[mid - 1] + poolCredits[mid]) / 2;
                neededCredits = medianCredits * neededCourses;
            }
        }

        // Cap completed courses and credits when courses_needed is set
        // If requirement needs N courses, only count the first N completed courses
        if (neededCourses > 0 && completedCourses.length > neededCourses) {
            // Calculate credits for only the first N courses
            let cappedCredits = 0;
            const cappedCourses: string[] = [];
            for (let i = 0; i < neededCourses && i < completedCourses.length; i++) {
                const code = completedCourses[i];
                cappedCourses.push(code);
                cappedCredits += this.getCourseCredits(code, creditsMap, userPrerequisites);
            }
            completedCourses = cappedCourses;
            creditsCompleted = cappedCredits;
        }

        // Apply capping logic for credits
        // Use estimatedCreditsForDisplay when neededCredits is 0 (for totals)
        // For course-based requirements, estimate credits using median * courses_needed
        // This ensures completion logic uses actual neededCredits (which stays 0 for course-based)
        let displayCreditsNeeded = neededCredits > 0 ? neededCredits : estimatedCreditsForDisplay;
        let displayCreditsCompleted = neededCredits > 0 ? Math.min(creditsCompleted, neededCredits) : creditsCompleted;
        let displayCreditsPlanned = neededCredits > 0
            ? Math.min(creditsPlanned, Math.max(0, neededCredits - displayCreditsCompleted))
            : creditsPlanned;

        const totalNeeded = neededCredits || neededCourses || 0;
        const totalCurrent = (neededCredits ? creditsCompleted : completedCourses.length) +
            (neededCredits ? creditsPlanned : plannedCourses.length);

        let status: 'completed' | 'in_progress' | 'remaining' = 'remaining';

        // Use original requirements for strict completion check to avoid issues with credit estimations
        const origCoursesNeeded = requirement.courses_needed || 0;
        const origCreditsNeeded = requirement.credits_needed || 0;

        if (origCoursesNeeded > 0 || origCreditsNeeded > 0) {
            let coursesMet = origCoursesNeeded > 0 ? (completedCourses.length >= origCoursesNeeded) : true;
            let creditsMet = origCreditsNeeded > 0 ? (creditsCompleted >= origCreditsNeeded) : true;

            if (coursesMet && creditsMet) {
                status = 'completed';
            } else if (totalCurrent > 0) {
                status = 'in_progress';
            }
        } else {
            // Fallback for estimated requirements
            if (neededCredits && creditsCompleted >= neededCredits) {
                status = 'completed';
            } else if (neededCourses && completedCourses.length >= neededCourses) {
                status = 'completed';
            } else if (totalCurrent > 0) {
                status = 'in_progress';
            }
        }

        // Align progress metrics with original requirements for consistency in percentages/bars
        const progressNeeded = origCreditsNeeded > 0 ? origCreditsNeeded : (origCoursesNeeded > 0 ? origCoursesNeeded : totalNeeded);
        const progressCompleted = origCreditsNeeded > 0 ? creditsCompleted : (origCoursesNeeded > 0 ? completedCourses.length : (neededCredits ? creditsCompleted : completedCourses.length));
        const progressPlanned = origCreditsNeeded > 0 ? creditsPlanned : (origCoursesNeeded > 0 ? plannedCourses.length : (neededCredits ? creditsPlanned : plannedCourses.length));
        const progressTotal = progressCompleted + progressPlanned;

        // Force 100% if status is completed to avoid rounding/estimation issues
        const progressPercent = status === 'completed' ? 100 : (progressNeeded > 0 ? Math.min(100, (progressTotal / progressNeeded) * 100) : 0);
        const completedPercent = status === 'completed' ? 100 : (progressNeeded > 0 ? Math.min(100, (progressCompleted / progressNeeded) * 100) : 0);
        const plannedPercent = progressNeeded > 0 ? Math.min(100, (progressPlanned / progressNeeded) * 100) : 0;


        return {
            requirementId: (requirement as any).id,
            title: (requirement as any).title,
            completed: status === 'completed',
            status,
            progress: progressPercent,
            completedProgress: completedPercent,
            plannedProgress: plannedPercent,
            creditsCompleted,
            creditsPlanned,
            completedCourses,
            plannedCourses,
            neededCourses,
            neededCredits,
            remainingCourses: Math.max(0, neededCourses - completedCourses.length - plannedCourses.length),
            remainingCredits: Math.max(0, neededCredits - creditsCompleted - creditsPlanned),
            displayCreditsNeeded,
            displayCreditsCompleted,
            displayCreditsPlanned
        };
    }

    /**
     * Calculate overall degree progress with support for overlapping requirements and OR logic.
     */
    static calculateDegreeProgress(
        degree: Degree,
        context: Parameters<typeof DegreeRequirementService.calculateRequirementProgress>[1]
    ): {
        requirementsProgress: RequirementProgress[];
        totalCreditsNeeded: number;
        totalCreditsCompleted: number;
        totalCreditsPlanned: number;
        percentComplete: number;
        requirementProgress: number;
        creditProgress: number;
        completedCourses: string[];
        remainingCourses: string[];
    } {
        const requirements = Array.isArray(degree.requirements) ? degree.requirements : (degree.requirements as any)?.requirements || [];
        const topLevelLogic = Array.isArray(degree.requirements) ? 'and' : (degree.requirements as any)?.logic || 'and';

        const requirementsProgress: RequirementProgress[] = [];
        let totalRequirementsCount = 0;
        let completedRequirementsCount = 0;
        let totalCreditsNeeded = 0;
        let totalCreditsCompleted = 0;
        let totalCreditsPlanned = 0;

        const allDegreeCourseCodes = new Set<string>();

        const processRequirements = (reqs: Requirement[], parentLogic: string = 'and', parentRequirement?: Requirement): RequirementProgress[] => {
            if (!reqs || !Array.isArray(reqs)) return [];

            const currentResults: RequirementProgress[] = [];

            if (parentLogic === 'or') {
                const hasTotalRequirement = parentRequirement && (parentRequirement.courses_needed || parentRequirement.credits_needed);

                if (hasTotalRequirement) {
                    let aggregatedCoursesCompleted = 0;
                    let aggregatedCreditsCompleted = 0;
                    let aggregatedCreditsPlanned = 0;
                    let aggregatedCreditsNeeded = 0;
                    const allCompletedCoursesSet = new Set<string>();

                    reqs.forEach(req => {
                        let progress: RequirementProgress;
                        if (req.requirements && req.requirements.length > 0) {
                            const subResults = processRequirements(req.requirements, req.logic || 'and', req);
                            progress = this.aggregateRequirementProgress(req, subResults);
                            requirementsProgress.push(progress);
                        } else {
                            progress = this.calculateRequirementProgress(req, context);
                            requirementsProgress.push(progress);
                        }

                        currentResults.push(progress);
                        totalRequirementsCount++;
                        if (progress.completed) completedRequirementsCount++;

                        if (progress.completedCourses.length > 0) {
                            progress.completedCourses.forEach(code => {
                                const norm = normalize(code);
                                if (!allCompletedCoursesSet.has(norm)) {
                                    allCompletedCoursesSet.add(norm);
                                    aggregatedCoursesCompleted++;
                                }
                            });
                        }
                        // Use displayCreditsCompleted (capped) to prevent over-counting
                        const progressCappedCredits = progress.displayCreditsCompleted ?? progress.creditsCompleted;
                        aggregatedCreditsCompleted += progressCappedCredits;
                        const progressCappedPlanned = progress.displayCreditsPlanned ?? progress.creditsPlanned;
                        aggregatedCreditsPlanned += progressCappedPlanned;
                        aggregatedCreditsNeeded += progress.displayCreditsNeeded || 0;
                    });

                    if (parentRequirement!.credits_needed && parentRequirement!.credits_needed > 0) {
                        totalCreditsNeeded += parentRequirement!.credits_needed!;
                        totalCreditsCompleted += Math.min(aggregatedCreditsCompleted, parentRequirement!.credits_needed!);
                        totalCreditsPlanned += Math.min(aggregatedCreditsPlanned, Math.max(0, parentRequirement!.credits_needed! - aggregatedCreditsCompleted));
                    } else if (aggregatedCreditsNeeded > 0) {
                        totalCreditsNeeded += aggregatedCreditsNeeded;
                        totalCreditsCompleted += Math.min(aggregatedCreditsCompleted, aggregatedCreditsNeeded);
                        totalCreditsPlanned += Math.min(aggregatedCreditsPlanned, Math.max(0, aggregatedCreditsNeeded - aggregatedCreditsCompleted));
                    }
                } else {
                    let minCreditsNeeded = Infinity;
                    let maxCreditsCompleted = 0;
                    let maxCreditsPlanned = 0;

                    reqs.forEach(req => {
                        let progress: RequirementProgress;
                        if (req.requirements && req.requirements.length > 0) {
                            const subResults = processRequirements(req.requirements, req.logic || 'and', req);
                            progress = this.aggregateRequirementProgress(req, subResults);
                            requirementsProgress.push(progress);
                        } else {
                            progress = this.calculateRequirementProgress(req, context);
                            requirementsProgress.push(progress);
                        }

                        currentResults.push(progress);
                        totalRequirementsCount++;
                        if (progress.completed) completedRequirementsCount++;

                        minCreditsNeeded = Math.min(minCreditsNeeded, progress.displayCreditsNeeded || 0);
                        if (progress.completed) {
                            // Use displayCreditsCompleted (capped) to prevent over-counting
                            const progressCappedCredits = progress.displayCreditsCompleted ?? progress.creditsCompleted;
                            maxCreditsCompleted = Math.max(maxCreditsCompleted, progressCappedCredits);
                            const progressCappedPlanned = progress.displayCreditsPlanned ?? progress.creditsPlanned;
                            maxCreditsPlanned = Math.max(maxCreditsPlanned, progressCappedPlanned);
                        }
                    });

                    if (minCreditsNeeded !== Infinity && minCreditsNeeded > 0) {
                        totalCreditsNeeded += minCreditsNeeded;
                        totalCreditsCompleted += Math.min(maxCreditsCompleted, minCreditsNeeded);
                        totalCreditsPlanned += Math.min(maxCreditsPlanned, Math.max(0, minCreditsNeeded - maxCreditsCompleted));
                    }
                }
            } else {
                const requirementsWithPools: Array<{
                    req: Requirement;
                    progress: RequirementProgress;
                    expandedPool: Set<string>;
                    creditsNeeded: number;
                }> = [];

                reqs.forEach(req => {
                    const hasSubRequirements = req.requirements && req.requirements.length > 0;
                    const hasCoursePool = req.course_pool && Array.isArray(req.course_pool) && req.course_pool.length > 0;

                    let progress: RequirementProgress;
                    if (hasSubRequirements && req.requirements) {
                        const subResults = processRequirements(req.requirements, req.logic || 'and', req);
                        progress = this.aggregateRequirementProgress(req, subResults);
                        requirementsProgress.push(progress);
                    } else {
                        progress = this.calculateRequirementProgress(req, context);
                        requirementsProgress.push(progress);
                    }

                    currentResults.push(progress);

                    // De-duplication logic (stays at leaf/pool level)
                    if (hasCoursePool && !hasSubRequirements) {
                        totalRequirementsCount++;
                        if (progress.completed) completedRequirementsCount++;

                        const expandedPool = new Set(this.expandPool(req, context.expandedPools, context.courseListCache, context.degreeMappings));
                        expandedPool.forEach(code => allDegreeCourseCodes.add(normalize(code)));

                        requirementsWithPools.push({
                            req,
                            progress,
                            expandedPool,
                            creditsNeeded: progress.displayCreditsNeeded || 0
                        });
                    } else if (hasSubRequirements) {
                        // For parent requirements in AND list, they might not have pools but still count toward requirement count
                        totalRequirementsCount++;
                        if (progress.completed) completedRequirementsCount++;
                    }
                });

                requirementsWithPools.sort((a, b) => b.creditsNeeded - a.creditsNeeded);
                const processedIndices = new Set<number>();

                for (let i = 0; i < requirementsWithPools.length; i++) {
                    if (processedIndices.has(i)) continue;
                    const current = requirementsWithPools[i];
                    let isSubset = false;

                    for (let j = 0; j < i; j++) {
                        if (processedIndices.has(j)) continue;
                        const larger = requirementsWithPools[j];

                        const currentPoolArray = Array.from(current.expandedPool);
                        if (currentPoolArray.length > 0 && Array.from(larger.expandedPool).length > 0) {
                            const allInLarger = currentPoolArray.every(c => larger.expandedPool.has(c));
                            if (allInLarger && current.creditsNeeded <= larger.creditsNeeded) {
                                isSubset = true;
                                processedIndices.add(i);
                                break;
                            }
                        }
                    }

                    if (!isSubset) {
                        if (current.creditsNeeded > 0) {
                            totalCreditsNeeded += current.creditsNeeded;
                            const cappedCredits = current.progress.displayCreditsCompleted ?? current.progress.creditsCompleted;
                            totalCreditsCompleted += Math.min(cappedCredits, current.creditsNeeded);
                            const cappedPlanned = current.progress.displayCreditsPlanned ?? current.progress.creditsPlanned;
                            totalCreditsPlanned += Math.min(cappedPlanned, Math.max(0, current.creditsNeeded - cappedCredits));
                        }
                    }
                }
            }
            return currentResults;
        };

        processRequirements(requirements, topLevelLogic);

        // Calculate progress using a better formula that handles overlapping requirements
        // The old formula could exceed 100% when courses satisfy multiple requirements

        // 1. Requirement-based progress: what percentage of requirements are completed
        const requirementProgress = totalRequirementsCount > 0
            ? (completedRequirementsCount / totalRequirementsCount) * 100
            : 0;

        // 2. Credit-based progress: unique credits completed vs needed
        // Use only completed credits (not planned) to avoid over-counting
        // Only calculate if we have meaningful credit requirements (avoid division by zero or tiny values)
        // If totalCreditsNeeded is 0 or very small, credit progress should be 0% (not 100%)
        const creditProgress = totalCreditsNeeded > 1 // Use > 1 to avoid issues with very small values
            ? Math.min((totalCreditsCompleted / totalCreditsNeeded) * 100, 100)
            : 0; // If no meaningful credit requirements, show 0% (prevents showing 100% incorrectly)

        // 3. Combined progress: weighted average favoring requirement completion
        // This prevents the 717% issue by capping and using both metrics
        // Weight: 60% requirement completion, 40% credit completion
        // This ensures that completing all requirements = 100%, even if credits exceed
        let percentComplete = 0;
        if (totalRequirementsCount > 0 && totalCreditsNeeded > 0) {
            // If all requirements are completed, cap at 100%
            if (completedRequirementsCount >= totalRequirementsCount) {
                percentComplete = 100;
            } else {
                // Weighted average, but never exceed requirement progress
                const weighted = (requirementProgress * 0.6) + (creditProgress * 0.4);
                percentComplete = Math.min(weighted, requirementProgress, 100);
            }
        } else if (totalRequirementsCount > 0) {
            // If no credit requirements, use requirement progress
            percentComplete = requirementProgress;
        } else if (totalCreditsNeeded > 0) {
            // If no requirement count, use credit progress (capped at 100%)
            percentComplete = Math.min(creditProgress, 100);
        }

        // Ensure we never exceed 100%
        percentComplete = Math.min(percentComplete, 100);

        // Completed courses filtering (simplified)
        const userCodeSet = new Set(context.userPrerequisites.flatMap(c => verifiedCourseCode(c.course_code) ?? []));
        const completedCourses = Array.from(allDegreeCourseCodes).filter(code => userCodeSet.has(normalize(code)));
        const remainingCourses = Array.from(allDegreeCourseCodes).filter(code => !userCodeSet.has(normalize(code)));

        return {
            requirementsProgress: requirementsProgress,
            totalCreditsNeeded,
            totalCreditsCompleted,
            totalCreditsPlanned,
            percentComplete,
            requirementProgress,
            creditProgress,
            completedCourses,
            remainingCourses
        };
    }

    /**
     * Aggregate sub-requirement progress into a parent progress object.
     */
    static aggregateRequirementProgress(
        requirement: Requirement,
        subResults: RequirementProgress[]
    ): RequirementProgress {
        const logic = requirement.logic || 'and';
        const coursesNeeded = requirement.courses_needed || (logic === 'or' ? 1 : subResults.length);
        const creditsNeeded = requirement.credits_needed || 0;

        let creditsCompleted = 0;
        let creditsPlanned = 0;
        let completedCourses: string[] = [];
        let plannedCourses: string[] = [];
        let completed = false;

        const allCompletedCourses = new Set<string>();
        const allPlannedCourses = new Set<string>();

        subResults.forEach(r => {
            r.completedCourses.forEach(c => allCompletedCourses.add(normalize(c)));
            r.plannedCourses.forEach(c => allPlannedCourses.add(normalize(c)));
        });

        completedCourses = Array.from(allCompletedCourses);
        plannedCourses = Array.from(allPlannedCourses).filter(c => !allCompletedCourses.has(c));

        if (logic === 'or') {
            const completedSubs = subResults.filter(r => r.completed);

            if (creditsNeeded > 0) {
                // For aggregated OR with credits, we sum up what children contributed
                creditsCompleted = subResults.reduce((sum, r) => sum + (r.displayCreditsCompleted ?? r.creditsCompleted), 0);
                creditsPlanned = subResults.reduce((sum, r) => sum + (r.displayCreditsPlanned ?? r.creditsPlanned), 0);
                completed = creditsCompleted >= creditsNeeded;
            } else {
                // For course-based or simple choice OR
                completed = completedSubs.length >= coursesNeeded;

                // For display stats in parent bar, we sum what children contributed
                creditsCompleted = subResults.reduce((sum, r) => sum + (r.displayCreditsCompleted ?? r.creditsCompleted), 0);
                creditsPlanned = subResults.reduce((sum, r) => sum + (r.displayCreditsPlanned ?? r.creditsPlanned), 0);
            }

            // Cap display stats by needed if specified
            if (creditsNeeded > 0) {
                creditsCompleted = Math.min(creditsCompleted, creditsNeeded);
                creditsPlanned = Math.min(creditsPlanned, Math.max(0, creditsNeeded - creditsCompleted));
            }
        } else {
            // AND logic
            completed = subResults.every(r => r.completed);
            creditsCompleted = subResults.reduce((sum, r) => sum + (r.displayCreditsCompleted ?? r.creditsCompleted), 0);
            creditsPlanned = subResults.reduce((sum, r) => sum + (r.displayCreditsPlanned ?? r.creditsPlanned), 0);

            if (creditsNeeded > 0) {
                creditsCompleted = Math.min(creditsCompleted, creditsNeeded);
                creditsPlanned = Math.min(creditsPlanned, Math.max(0, creditsNeeded - creditsCompleted));
            }
        }

        const totalNeeded = creditsNeeded || coursesNeeded || 1;
        const currentCompletedCount = (creditsNeeded ? creditsCompleted : completedCourses.length);
        const currentPlannedCount = (creditsNeeded ? creditsPlanned : plannedCourses.length);
        const totalCurrentCount = currentCompletedCount + currentPlannedCount;

        const progressPercent = Math.min(100, (totalCurrentCount / totalNeeded) * 100);
        const completedPercent = Math.min(100, (currentCompletedCount / totalNeeded) * 100);
        const plannedPercent = Math.min(100, (currentPlannedCount / totalNeeded) * 100);

        let status: 'completed' | 'in_progress' | 'remaining' = 'remaining';
        if (completed) status = 'completed';
        else if (totalCurrentCount > 0) status = 'in_progress';

        return {
            requirementId: (requirement as any).id,
            title: requirement.title,
            completed,
            status,
            progress: progressPercent,
            completedProgress: completedPercent,
            plannedProgress: plannedPercent,
            creditsCompleted,
            creditsPlanned,
            completedCourses,
            plannedCourses,
            neededCourses: coursesNeeded,
            neededCredits: creditsNeeded,
            remainingCourses: Math.max(0, coursesNeeded - completedCourses.length - plannedCourses.length),
            remainingCredits: Math.max(0, (creditsNeeded || 0) - creditsCompleted - creditsPlanned),
            displayCreditsNeeded: creditsNeeded || 0,
            displayCreditsCompleted: creditsCompleted,
            displayCreditsPlanned: creditsPlanned
        };
    }

    /**
     * Helper to load a list through the API if not in cache.
     */
    static async ensureListLoaded(
        listId: number,
        courseListCache: Map<number, { name: string; classes: string[] }>,
        staticCourseListCache?: Map<number, { name: string; classes: string[] }>,
        setCache?: (id: number, data: { name: string; classes: string[] }) => void
    ): Promise<{ name: string; classes: string[] }> {
        if (courseListCache.has(listId)) return courseListCache.get(listId)!;

        if (staticCourseListCache?.has(listId)) {
            const data = staticCourseListCache.get(listId)!;
            setCache?.(listId, data);
            return data;
        }

        try {
            const data = await courseListsApi.get(listId);
            const classes = (data.classes || []).map((c: any) => normalize(c));
            const result = { name: data.name, classes };
            setCache?.(listId, result);
            return result;
        } catch (e) {
            console.error(`Failed to load course list ${listId}`, e);
            return { name: `List ${listId}`, classes: [] };
        }
    }
}
